import React, { useState, useEffect, useCallback } from 'react';
import recruitingService from '../utils/recruitingService';
import KPICard from '../components/KPICard';
import FilterBar from '../components/FilterBar';
import PipelineStageChart from '../components/PipelineStageChart';
import JobsStatusChart from '../components/JobsStatusChart';
import ActiveCandidatesTable from '../components/ActiveCandidatesTable';
import './PipelineDashboard.css';

/**
 * PipelineDashboard Component
 * Operational view of active jobs and candidates
 */
function PipelineDashboard() {
    // Filter state
    const [filters, setFilters] = useState({
        recruiter: null,
        department: null,
        jobStatus: null
    });

    // Data state
    const [summary, setSummary] = useState(null);
    const [pipelineByStage, setPipelineByStage] = useState([]);
    const [filterOptions, setFilterOptions] = useState({
        recruiters: [],
        departments: [],
        jobStatuses: ['Open', 'In Progress', 'On Hold', 'Filled', 'Closed', 'Cancelled']
    });

    // Loading states
    const [loading, setLoading] = useState({
        summary: true,
        pipeline: true
    });

    // Error state
    const [error, setError] = useState(null);

    // Fetch filter options
    const fetchFilterOptions = useCallback(async () => {
        try {
            const response = await recruitingService.getFilterOptions();
            if (response.success) {
                setFilterOptions(prev => ({
                    ...prev,
                    recruiters: response.data.recruiters || [],
                    departments: response.data.departments || []
                }));
            }
        } catch (err) {
            console.error('Error fetching filter options:', err);
        }
    }, []);

    // Fetch dashboard data
    const fetchDashboardData = useCallback(async () => {
        setError(null);

        // Fetch pipeline summary
        setLoading(prev => ({ ...prev, summary: true }));
        try {
            const response = await recruitingService.getPipelineSummary(filters);
            if (response.success) {
                setSummary(response.data);
            }
        } catch (err) {
            console.error('Error fetching pipeline summary:', err);
            setError('Failed to load pipeline summary');
        }
        setLoading(prev => ({ ...prev, summary: false }));

        // Fetch pipeline by stage (with filters)
        setLoading(prev => ({ ...prev, pipeline: true }));
        try {
            const response = await recruitingService.getPipelineByStage(filters);
            if (response.success) {
                setPipelineByStage(response.data);
            }
        } catch (err) {
            console.error('Error fetching pipeline by stage:', err);
        }
        setLoading(prev => ({ ...prev, pipeline: false }));
    }, [filters]);

    // Initial load
    useEffect(() => {
        fetchFilterOptions();
        fetchDashboardData();
    }, [fetchFilterOptions, fetchDashboardData]);

    // Handle filter changes
    const handleApplyFilters = () => {
        fetchDashboardData();
    };

    const handleResetFilters = () => {
        const resetFilters = {
            recruiter: null,
            department: null,
            jobStatus: null
        };
        setFilters(resetFilters);
        // Fetch data with reset filters immediately
        fetchDataWithFilters(resetFilters);
    };

    // Helper to fetch data with specific filters (for reset)
    const fetchDataWithFilters = async (filterValues) => {
        setError(null);

        setLoading(prev => ({ ...prev, summary: true, pipeline: true }));
        try {
            const [summaryResponse, pipelineResponse] = await Promise.all([
                recruitingService.getPipelineSummary(filterValues),
                recruitingService.getPipelineByStage(filterValues)
            ]);

            if (summaryResponse.success) {
                setSummary(summaryResponse.data);
            }
            if (pipelineResponse.success) {
                setPipelineByStage(pipelineResponse.data);
            }
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load dashboard data');
        }
        setLoading({ summary: false, pipeline: false });
    };

    // Calculate jobs by status for donut chart
    const jobsStatusData = summary ? [
        { name: 'Open', value: summary.openJobs || 0, color: '#27AE60' },
        { name: 'In Progress', value: summary.inProgressJobs || 0, color: '#E67E22' },
        { name: 'Other', value: Math.max(0, (summary.totalJobs || 0) - (summary.openJobs || 0) - (summary.inProgressJobs || 0)), color: '#95A5A6' }
    ].filter(item => item.value > 0) : [];

    return (
        <div className="pipeline-dashboard">
            <div className="dashboard-header">
                <div className="dashboard-title-section">
                    <h1 className="dashboard-title">Pipeline Dashboard</h1>
                    <p className="dashboard-subtitle">Active Jobs & Candidates Overview</p>
                </div>
            </div>

            {/* Filters - Single Line */}
            <div className="pipeline-filter-bar">
                <div className="filter-item">
                    <label className="filter-label">Recruiter</label>
                    <select
                        className="filter-select"
                        value={filters.recruiter || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, recruiter: e.target.value || null }))}
                    >
                        <option value="">All Recruiters</option>
                        {filterOptions.recruiters.map((recruiter) => (
                            <option key={recruiter} value={recruiter}>{recruiter}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-item">
                    <label className="filter-label">Department</label>
                    <select
                        className="filter-select"
                        value={filters.department || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value || null }))}
                    >
                        <option value="">All Departments</option>
                        {filterOptions.departments.map((dept) => (
                            <option key={dept} value={dept}>{dept}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-item">
                    <label className="filter-label">Job Status</label>
                    <select
                        className="filter-select"
                        value={filters.jobStatus || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, jobStatus: e.target.value || null }))}
                    >
                        <option value="">All Statuses</option>
                        {filterOptions.jobStatuses.map((status) => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-spacer"></div>
                <div className="filter-bar-actions">
                    <button className="filter-btn filter-btn-apply" onClick={handleApplyFilters}>
                        Apply
                    </button>
                    <button className="filter-btn filter-btn-reset" onClick={handleResetFilters}>
                        Reset
                    </button>
                </div>
            </div>

            {error && (
                <div className="dashboard-error">
                    <p>{error}</p>
                    <button onClick={fetchDashboardData}>Retry</button>
                </div>
            )}

            {/* KPI Cards Row */}
            <div className="kpi-grid kpi-grid-5">
                <KPICard
                    title="Total Jobs"
                    value={summary?.totalJobs || 0}
                    loading={loading.summary}
                    icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                        </svg>
                    }
                />
                <KPICard
                    title="Open Jobs"
                    value={summary?.openJobs || 0}
                    status="good"
                    loading={loading.summary}
                    icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="16"></line>
                            <line x1="8" y1="12" x2="16" y2="12"></line>
                        </svg>
                    }
                />
                <KPICard
                    title="Jobs w/o Candidates"
                    value={summary?.jobsWithoutCandidates || 0}
                    status={(summary?.jobsWithoutCandidates || 0) > 5 ? 'danger' : (summary?.jobsWithoutCandidates || 0) > 0 ? 'warning' : 'good'}
                    subtitle="Need sourcing"
                    loading={loading.summary}
                    icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                    }
                />
                <KPICard
                    title="Active Candidates"
                    value={summary?.activeCandidates || 0}
                    loading={loading.summary}
                    icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                    }
                />
                <KPICard
                    title="Submitted to HM"
                    value={summary?.submittedToHM || 0}
                    status="good"
                    subtitle="Awaiting interview"
                    loading={loading.summary}
                    icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 2L11 13"></path>
                            <path d="M22 2l-7 20-4-9-9-4 20-7z"></path>
                        </svg>
                    }
                />
            </div>

            {/* Pipeline by Stage - Full Width */}
            <div className="chart-section">
                <PipelineStageChart
                    data={pipelineByStage}
                    loading={loading.pipeline}
                />
            </div>

            {/* Charts Row */}
            <div className="charts-grid charts-grid-2">
                <JobsStatusChart
                    data={jobsStatusData}
                    totalJobs={summary?.totalJobs || 0}
                    loading={loading.summary}
                />
                <div className="chart-container stage-time-chart">
                    <div className="chart-header">
                        <h3 className="chart-title">Avg Days in Stage</h3>
                        <span className="chart-subtitle">Time candidates spend at each stage vs target</span>
                    </div>
                    <div className="stage-time-legend">
                        <span className="legend-item"><span className="legend-dot good"></span> On Track</span>
                        <span className="legend-item"><span className="legend-dot warning"></span> Attention</span>
                        <span className="legend-item"><span className="legend-dot danger"></span> At Risk</span>
                        <span className="legend-item"><span className="legend-target"></span> Target</span>
                    </div>
                    <div className="stage-time-bars">
                        {(() => {
                            // Stage-specific target days
                            const stageTargets = {
                                'New': 3,
                                'New Lead': 5,
                                'Phone Screen': 7,
                                'Submittal': 5,
                                'Submitted to HM': 7,
                                'HM 1st Interview': 10,
                                'HM Interview': 10,
                                'HM 2nd Interview': 7,
                                'Final Interview': 7,
                                'Market Visit': 5,
                                'Offer Extended': 5,
                                'Offer Accepted': 3
                            };

                            // Get max value for scaling
                            const maxDays = Math.max(...pipelineByStage.map(s => s.avgDaysInStage), 1);
                            const maxTarget = Math.max(...Object.values(stageTargets));
                            const scaleMax = Math.max(maxDays, maxTarget) * 1.1; // Add 10% padding

                            return pipelineByStage.map((stage, index) => {
                                const target = stageTargets[stage.stage] || 7;
                                const actual = stage.avgDaysInStage;
                                const ratio = actual / target;

                                // Status based on ratio to target
                                let status = 'good';
                                if (ratio > 2) status = 'danger';
                                else if (ratio > 1.3) status = 'warning';

                                const barWidth = (actual / scaleMax) * 100;
                                const targetPosition = (target / scaleMax) * 100;

                                return (
                                    <div key={index} className="stage-time-row-enhanced">
                                        <span className="stage-name">{stage.stage}</span>
                                        <div className="stage-bar-wrapper">
                                            <div className="stage-bar-track">
                                                <div
                                                    className={`stage-bar-fill ${status}`}
                                                    style={{ width: `${Math.min(100, barWidth)}%` }}
                                                >
                                                    <span className="bar-value">{actual}d</span>
                                                </div>
                                                <div
                                                    className="stage-target-marker"
                                                    style={{ left: `${Math.min(100, targetPosition)}%` }}
                                                    title={`Target: ${target} days`}
                                                >
                                                    <span className="target-label">{target}d</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`stage-status-badge ${status}`}>
                                            {ratio <= 1 ? '✓' : ratio <= 1.3 ? '!' : '!!'}
                                        </div>
                                    </div>
                                );
                            });
                        })()}
                        {pipelineByStage.length === 0 && !loading.pipeline && (
                            <p className="no-data">No stage data available</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Active Candidates Table */}
            <div className="table-section">
                <ActiveCandidatesTable
                    filters={filters}
                />
            </div>
        </div>
    );
}

export default PipelineDashboard;
